// src/pages/OpportunitesFoodinnov.tsx
import React, { useState, useEffect, useMemo } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import {
  doc,
  onSnapshot,
  updateDoc,
  arrayRemove,
  arrayUnion,
  setDoc,
} from "firebase/firestore";
import "tippy.js/dist/tippy.css";
import Tippy from "@tippyjs/react";
import "../styles/Modal.css";
import "../styles/Opportunites.css";
import OpportuniteFoodinnovModal, {
  FirestoreOpportunity,
} from "../components/OpportuniteFoodinnovModal";
import TypeDemandeModal from "../components/TypeDemandeModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { getUserRole } from "../utils/authUtils";
import { usePageTitle } from '../hooks/usePageTitle';
import { logUserAction } from "../utils/logUtils";
import BillingRecapTable from "../components/BillingRecapTable";

interface CompaniesData {
  [companyName: string]: {
    opportunities?: FirestoreOpportunity[];
    principalContact?: string;
    principalFirstName?: string;
    principalGender?: string;
    principalEmail?: string;
    principalPhone?: string;
    principalPosition?: string;
    otherContacts?: {
      civilite?: string;
      prenom?: string;
      nom: string;
    }[];
  };
}

const OpportunitesFoodinnov: React.FC = () => {
  usePageTitle("Demandes Foodinnov");
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [preselectedCompany, setPreselectedCompany] = useState<string | null>(null);
  const [contactModal, setContactModal] = useState<{
    name: string;
    email?: string;
    phone?: string;
    fonction?: string;
    gender?: string;
    principalContact?: string;
    principalFirstName?: string;
  } | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMonth, setFilterMonth] = useState((new Date().getMonth() + 1).toString());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [sortBy, setSortBy] = useState<"date" | "amount" | "alphabetical">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [opportunities, setOpportunities] = useState<FirestoreOpportunity[]>([]);
  const [editingOpportunity, setEditingOpportunity] = useState<FirestoreOpportunity | null>(null);
  const [companiesMap, setCompaniesMap] = useState<CompaniesData>({});
  const [user, setUser] = useState(auth.currentUser);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [opportunityTypes, setOpportunityTypes] = useState<string[]>([]);
  const [showTypeDemandeModal, setShowTypeDemandeModal] = useState(false);

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  // Détecter la société pré-sélectionnée depuis la navigation
  useEffect(() => {
    const state = location.state as { preselectedCompany?: string } | null;
    if (state?.preselectedCompany) {
      setPreselectedCompany(state.preselectedCompany);
      setShowModal(true);
      // Nettoyer le state pour éviter de rouvrir le modal à chaque re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser && currentUser.email) {
        const role = await getUserRole(currentUser.email);
        setUserRole(role);
      } else {
        setUserRole(null);
      }
    });

    const ref = doc(db, "prospects", "foodinnovCompanies");
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as CompaniesData;
        const all: FirestoreOpportunity[] = [];
        for (const name in data) {
          const rec = data[name];
          if (Array.isArray(rec.opportunities)) {
            rec.opportunities.forEach((opp) =>
              all.push({ ...opp, companyId: opp.companyId || name })
            );
          }
        }
        setOpportunities(all);
        setCompaniesMap(data);
        
        // Charger les types de demande depuis les données
        let types = (data.opportunityTypes as string[]) || [];
        
        // Si pas de types dans opportunityTypes, extraire depuis les opportunités existantes
        if (types.length === 0) {
          const existingTypes = new Set<string>();
          all.forEach(opp => {
            if (opp.type) {
              existingTypes.add(opp.type);
            }
          });
          types = Array.from(existingTypes).sort();
          
          // Sauvegarder automatiquement les types extraits
          if (types.length > 0) {
            updateDoc(ref, {
              opportunityTypes: types
            }).then(() => {
              console.log("Types extraits et sauvegardés pour Foodinnov:", types);
            }).catch(error => {
              console.error("Erreur lors de la sauvegarde des types Foodinnov:", error);
            });
          }
        }
        
        console.log("Types chargés dans useEffect Foodinnov:", types);
        setOpportunityTypes(types);
      } else {
        setOpportunities([]);
        setOpportunityTypes([]);
      }
    });
    return () => { unsubscribeAuth(); unsub(); };
  }, []);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return opportunities
      .filter((o) => {
        const oppDate = new Date(o.date);
        const matchMonth = filterMonth ? oppDate.getMonth() + 1 === parseInt(filterMonth) : true;
        const matchYear = filterYear ? oppDate.getFullYear() === parseInt(filterYear) : true;
        const matchType = filterType ? o.type === filterType : true;
        const matchStatus = filterStatus ? (o.status || 'En Cours') === filterStatus : true;
        return (
          (o.companyId.toLowerCase().includes(q) ||
            o.contact.toLowerCase().includes(q) ||
            o.type.toLowerCase().includes(q) ||
            o.amount.toString().includes(q)) &&
          matchMonth &&
          matchYear &&
          matchType &&
          matchStatus
        );
      })
      .sort((a, b) => {
        const direction = sortOrder === "asc" ? 1 : -1;
        if (sortBy === "date") {
          return (new Date(a.date).getTime() - new Date(b.date).getTime()) * direction;
        } else if (sortBy === "amount") {
          return ((a.amount ?? 0) - (b.amount ?? 0)) * direction;
        } else {
          return a.companyId.localeCompare(b.companyId) * direction;
        }
      });
  }, [opportunities, searchTerm, filterMonth, filterYear, filterType, filterStatus, sortBy, sortOrder]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalHT = filtered.reduce((acc, o) => acc + (o.amount || 0), 0);
  const totalTTC = filtered.reduce((acc, o) => acc + (o.ttc ?? o.amount * 1.2), 0);

  const openNew = () => {
    setEditingOpportunity(null);
    setShowModal(true);
  };

  const openEdit = (o: FirestoreOpportunity) => {
    setEditingOpportunity(o);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setPreselectedCompany(null);
  };

  const handleSave = async (data: FirestoreOpportunity) => {
    try {
      const ref = doc(db, "prospects", "foodinnovCompanies");
      await setDoc(ref, {}, { merge: true });
      await updateDoc(ref, {
        [`${data.companyId}.opportunities`]: arrayUnion({
          ...data,
          createdAt: new Date(),
        }),
      });
      
      // Log de l'ajout
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Ajout opportunité Foodinnov",
          `Nouvelle opportunité Foodinnov ajoutée pour ${data.companyId} - Type: ${data.type} - Montant: ${data.amount}€`
        );
      }
      
      toast.success("Demande ajoutée avec succès !");
    } catch (e: any) {
      console.error("Erreur ajout opportunité :", e);
      toast.error(`Échec de l'ajout : ${e.message}`);
    } finally {
      setShowModal(false);
    }
  };

  const handleUpdate = async (oldOpp: FirestoreOpportunity, newOpp: FirestoreOpportunity) => {
    try {
      const ref = doc(db, "prospects", "foodinnovCompanies");
      await setDoc(ref, {}, { merge: true });
      await updateDoc(ref, {
        [`${oldOpp.companyId}.opportunities`]: arrayRemove(oldOpp),
      });
      await updateDoc(ref, {
        [`${newOpp.companyId}.opportunities`]: arrayUnion({
          ...newOpp,
          createdAt: oldOpp.createdAt || new Date(),
          updatedAt: new Date(),
        }),
      });
      
      // Log de la modification
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Modification opportunité Foodinnov",
          `Opportunité Foodinnov modifiée pour ${newOpp.companyId} - Type: ${newOpp.type} - Montant: ${newOpp.amount}€`
        );
      }
      
      toast.success("Demande mise à jour avec succès !");
    } catch (e: any) {
      console.error("Erreur mise à jour opportunité :", e);
      toast.error(`Échec de la mise à jour : ${e.message}`);
    } finally {
      setShowModal(false);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState<FirestoreOpportunity | null>(null);

  const handleDelete = async (opp: FirestoreOpportunity) => {
    setOpportunityToDelete(opp);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (opportunityToDelete) {
      try {
        const ref = doc(db, "prospects", "foodinnovCompanies");
        await updateDoc(ref, {
          [`${opportunityToDelete.companyId}.opportunities`]: arrayRemove(opportunityToDelete),
        });
        
        // Log de la suppression
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Suppression opportunité Foodinnov",
            `Opportunité Foodinnov supprimée pour ${opportunityToDelete.companyId} - Type: ${opportunityToDelete.type} - Montant: ${opportunityToDelete.amount}€`
          );
        }
        
        toast.success("Demande supprimée avec succès !");
      } catch (e: any) {
        console.error("Erreur suppression :", e);
        toast.error(`Échec de la suppression : ${e.message}`);
      } finally {
        setShowDeleteModal(false);
        setOpportunityToDelete(null);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setOpportunityToDelete(null);
  };

  const openContactModal = (opp: FirestoreOpportunity) => {
    const companyInfo = companiesMap[opp.companyId];
    setContactModal({
      name: opp.contact ?? "Contact inconnu",
      email: companyInfo?.principalEmail ?? "Non renseigné",
      phone: companyInfo?.principalPhone ?? "Non renseigné",
      fonction: companyInfo?.principalPosition ?? "",
      gender: companyInfo?.principalGender,
      principalContact: companyInfo?.principalContact,
      principalFirstName: companyInfo?.principalFirstName,
    });
  };

  const closeContactModal = () => setContactModal(null);

  const handleRowClick = (row: any) => {
    navigate(`/opportuniteFoodinnov/${row.id}`);
  };

  return (
    <div className="opportunites-container">
      <Header
        isAuthenticated={!!user}
        onLoginClick={() => navigate('/login')}
        onHomeClick={() => navigate('/')}
        onLogoutClick={handleLogout}
      />
      <h2 className="opportunites-title" style={{ color: '#424272' }}>Liste des Demandes Foodinnov :</h2>

      <div className="opportunites-actions-bar">
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          {userRole && userRole !== 'foodinnov' && (
            <button 
              className="add-opportunite-button" 
              onClick={openNew}
              style={{ backgroundColor: '#424272' }}
            >
              + Ajouter une demande
            </button>
          )}
          {userRole === 'admin' && (
            <button 
              className="add-opportunite-button" 
              onClick={() => setShowTypeDemandeModal(true)}
              style={{ backgroundColor: '#424272' }}
            >
              + Ajouter un type de demande
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="opportunites-search-bar filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">Filtrer par type --</option>
            {[...opportunityTypes].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })).map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>

        <select className="opportunites-search-bar filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">Filtrer par statut --</option>
          <option value="En Cours">En Cours</option>
          <option value="Validé">Validé</option>
          <option value="Annulé">Annulé</option>
        </select>
        <input type="text" className="opportunites-search-bar" placeholder="Rechercher…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <button type="button" title="Mois précédent" onClick={() => { if (filterMonth === '') return; const m = parseInt(filterMonth); setFilterMonth(String(m === 1 ? 12 : m - 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <select className="opportunites-search-bar" style={{ maxWidth: "130px" }} value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
            <option value="">Afficher Tout</option>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={(i + 1).toString()}>
                {new Date(0, i).toLocaleString("fr-FR", { month: "long" }).replace(/^\w/, c => c.toUpperCase())}
              </option>
            ))}
          </select>
          <button type="button" title="Mois suivant" onClick={() => { if (filterMonth === '') return; const m = parseInt(filterMonth); setFilterMonth(String(m === 12 ? 1 : m + 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <select className="opportunites-search-bar" style={{ maxWidth: "120px" }} value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
          <option value="">Afficher Tout</option>
          {[2024, 2025, 2026].map((year) => (
            <option key={year} value={year.toString()}>{year}</option>
          ))}
        </select>
        <select className="opportunites-search-bar" style={{ maxWidth: "160px" }} value={sortBy} onChange={(e) => setSortBy(e.target.value as "date" | "amount" | "alphabetical")}>
          <option value="alphabetical">Alphabétique</option>
          <option value="date">Date de création</option>
          <option value="amount">Montant (HT)</option>
        </select>
        <button 
          style={{ 
            margin: "0 0.5rem", 
            padding: "0.3rem 0.6rem", 
            background: "#424272", 
            color: "white", 
            border: "none", 
            borderRadius: "4px" 
          }} 
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
        >
          {sortOrder === "asc" ? "↑" : "↓"}
        </button>
        <select className="opportunites-search-bar" style={{ maxWidth: "130px" }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
          <option value="10">10 par page</option>
          <option value="15">15 par page</option>
          <option value="25">25 par page</option>
          <option value="50">50 par page</option>
        </select>
        </div>
      </div>

      <table className="opportunites-table">
        <thead>
          <tr>
            <th>Nom de la société</th>
            <th>Contact</th>
            <th>Type de demande</th>
            <th>Quantité</th>
            <th>Prix Unitaire (HT)</th>
            <th>Remise (%)</th>
            <th>Montant (HT)</th>
            <th>Montant (TTC)</th>
            <th>Date</th>
            <th>Statut</th>
            {userRole === 'admin' && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {paginated.map((o, i) => (
            <tr key={i}>
              <td>{o.companyId}</td>
              <td>
                <button onClick={() => openContactModal(o)} style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}>
                  {o.contact}
                </button>
              </td>
              <td>
                <Tippy content={o.description || "Aucune description"} placement="top">
                  <span style={{ cursor: "help", textDecoration: "underline dotted" }}>
                    {o.type} {o.description && <span style={{ fontSize: "0.9rem", color: "#007bff" }}>ℹ️</span>}
                  </span>
                </Tippy>
              </td>
              <td>{o.qte ?? "-"}</td>
              <td>{o.pu ? `${o.pu.toFixed(2)} €` : "-"}</td>
              <td>{o.remise !== undefined ? `${o.remise} %` : "0 %"}</td>
              <td>{o.amount ? o.amount.toFixed(2) : "0.00"} €</td>
              <td>{(o.ttc ?? ((o.qte ?? 1) * (o.pu ?? 0) * (1 - (o.remise ?? 0) / 100) * 1.2)).toFixed(2)} €</td>
              <td>{new Date(o.date).toLocaleDateString("fr-FR")}</td>
              <td>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  backgroundColor: o.status === 'Validé' ? '#28a745' : 
                                o.status === 'Annulé' ? '#dc3545' : 
                                o.status === 'En Cours' ? '#ffc107' : '#6c757d',
                  color: o.status === 'Validé' ? 'white' : 
                         o.status === 'Annulé' ? 'white' : 
                         o.status === 'En Cours' ? 'black' : 'white'
                }}>
                  {o.status || 'En Cours'}
                </span>
              </td>
              {userRole === 'admin' && (
                <td>
                  <button className="edit-button" onClick={() => openEdit(o)}><i className="bi bi-pencil-square"></i></button>
                  <button className="delete-button" onClick={() => handleDelete(o)}><i className="bi bi-trash"></i></button>
                  <button 
                    className="edit-button" 
                    onClick={() => navigate('/devis-foodinnov', { state: { preselectedOpportunity: o } })} 
                    title="Créer un devis pour cette opportunité"
                    style={{ marginLeft: '5px' }}
                  >
                    <i className="bi bi-arrow-right"></i>
                  </button>
                </td>
              )}
            </tr>
          ))}
          <tr style={{ fontWeight: "bold", background: "#f8f8f8" }}>
            <td colSpan={5}>Total</td>
            <td></td>
            <td>{filtered.reduce((acc, o) => acc + (o.amount || 0), 0).toFixed(2)} €</td>
            <td>{filtered.reduce((acc, o) => acc + ((o.ttc ?? ((o.qte ?? 1) * (o.pu ?? 0) * (1 - (o.remise ?? 0) / 100) * 1.2))), 0).toFixed(2)} €</td>
            <td colSpan={userRole === 'admin' ? 3 : 2}></td>
          </tr>
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>← Précédent</button>
          <span>Page {currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Suivant →</button>
        </div>
      )}

      {showModal && (
        <OpportuniteFoodinnovModal
          show={showModal}
          handleClose={closeModal}
          onSave={handleSave}
          onUpdate={handleUpdate}
          editingOpportunity={editingOpportunity}
          opportunityTypes={opportunityTypes}
          preselectedCompanyId={preselectedCompany}
        />
      )}

      <TypeDemandeModal
        show={showTypeDemandeModal}
        handleClose={() => setShowTypeDemandeModal(false)}
        currentTypes={opportunityTypes}
        collection="foodinnovCompanies"
        onTypesUpdated={(newTypes) => {
          console.log("Callback onTypesUpdated appelé avec les types Foodinnov:", newTypes);
          setOpportunityTypes(newTypes);
        }}
      />

      {contactModal && (
        <div className="contact-popup-overlay" onClick={closeContactModal}>
          <div className="contact-popup-card" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup-btn" onClick={closeContactModal}>✕</button>
            <div className="contact-popup-content">
              <div className={`contact-avatar ${
                contactModal.gender === "Mme" ? "avatar-female"
                : contactModal.gender === "Mr" ? "avatar-male"
                : "avatar-neutral"
              }`}>
                {((contactModal.principalContact || "").charAt(0) + (contactModal.principalFirstName || "").charAt(0)).toUpperCase()}
              </div>
              <h3>{contactModal.name}</h3>
              {contactModal.fonction && <p className="contact-role">{contactModal.fonction}</p>}
              <hr />
              <div className="contact-info">
                <p><strong>Téléphone :</strong> {contactModal.phone || "Non renseigné"}</p>
                <p><strong>Email :</strong> {contactModal.email || "Non renseigné"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && opportunityToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer l'opportunité pour la société "{opportunityToDelete.companyId}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDelete}>Annuler</button>
              <button className="delete-button" onClick={confirmDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {userRole === 'admin' && <BillingRecapTable />}

      <ToastContainer position="top-right" autoClose={3000} />
      <Footer />
    </div>
  );
};

export default OpportunitesFoodinnov;
